import { ExpositionTimeSlotPicker } from 'containers/ExpositionTimeSlotPicker/ExpositionTimeSlotPicker';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import { useOrderContext } from 'lib/api/OrderContext';
import { useStreetContext } from 'lib/api/StreetContext';
import { fetchExpositionTimeslotPrices, fetchSoldoutDates } from 'lib/api/api';
import {
    filterOrderLinesByVenues,
    getExpositionGuidsByOrderLines,
    getVenueByExpositionGuid,
    mapOrderLinesByExposition,
    mergeOrderLine,
} from 'lib/api/context';
import { inputChangeEventFactory } from 'lib/events/eventFactory';
import { formatDate } from 'lib/format/date';
import { useLanguage } from 'lib/routes/useLanguage';
import {
    IIncompleteOrder,
    IIncompleteOrderLine,
    IOrderLine,
    IStreetData,
} from 'lib/types/api';

import { Datepicker } from 'components/Form/Datepicker/Datepicker';
import { IFieldProps } from 'components/Form/IFormFieldProps';
import { IOption } from 'components/Form/SelectField/SelectField';
import { Content } from 'components/Layout/Content/Content';
import { H2 } from 'components/Typography/H2/H2';

import styles from './VenueDateAndTimePicker.module.css';

export interface IVenueDateAndTimePickerProps
    extends IFieldProps<IIncompleteOrderLine[], CustomEvent> {
    /** The form field name. */
    name: string;
}

/**
 * Allows the user to select a date, and then a timeslot for each exposition found in
 * `orderContext.exposition_orderlines`.
 */
export const VenueDateAndTimePicker: React.FC<IVenueDateAndTimePickerProps> = ({
    name,
    onChange,
    value = [],
}) => {
    const [street] = useStreetContext() as [IStreetData, unknown];
    const [orderContext, setOrderContext] = useOrderContext();
    const language = useLanguage();

    // The value of contains the selected `IOrderLine[]` as stored in the form context, which at best contains the
    // references to the timeslots, but not the raw date. We need tot obtain the (selected) date elsewhere.
    const { values } = useFormikContext<IIncompleteOrder>();
    const dateString = values.date;

    const initialSelectedDate = dateString ? new Date(dateString) : new Date();
    const [selectedDateState, setSelectedDateState] = useState<Date>(initialSelectedDate);

    // We can only fetch sold out dates in the future, so the "from date" for the `fetchSoldoutDates` call is the
    // highest of:
    //
    // - The current date.
    // - The first date of the viewed month (activeStartDateState).
    const firstDayOfSelectedMonthDate = new Date(selectedDateState);
    firstDayOfSelectedMonthDate.setDate(1);
    const [activeStartDateState, setActiveStartDateState] = useState<Date>(
        firstDayOfSelectedMonthDate
    );
    const [isLoadingDate, setIsLoadingDate] = useState<boolean>(false);
    const [disabledDatesState, setDisabledDatesState] = useState<Date[]>([]);

    // We only want to see timeslot pickers for venues here.
    const venueOrderLines = filterOrderLinesByVenues(value, street.venues);
    const venueOrderLinesByExposition = mapOrderLinesByExposition(venueOrderLines);

    /**
     * If no date is selected, we want to auto-select the next available date.
     * This makes it easier and clearer for the user where the "start" is. It'll also jump to the correct month.
     * @see Taiga issue #2540 for details
     */
    function setFirstAvailableDate(
        disabledDatesList: Date[],
        setSelectedDateState: (d: Date) => void,
        onDateChange: (e: CustomEvent<{ value: Date }>) => void
    ) {
        if (disabledDatesList.length === 0) return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const disabledSet = new Set(
            disabledDatesList.map((d) =>
                new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString()
            )
        );

        // Today could be a candidate, so we start from there
        const candidate = new Date(today);
        // Keep incrementing the candidate date until we find one that is not disabled
        while (disabledSet.has(candidate.toISOString())) {
            candidate.setDate(candidate.getDate() + 1);
        }

        setSelectedDateState(candidate);
        const synthetic = new CustomEvent<{ value: Date }>('change', {
            detail: { value: candidate },
        });
        onDateChange(synthetic);
    }

    /**
     * Receive the (future only) sold out dates whenever the value changes.
     */
    useEffect(() => {
        const abortController = new AbortController();
        setIsLoadingDate(true);

        const currentDate = new Date();
        const fromDate =
            activeStartDateState > currentDate ? activeStartDateState : currentDate;

        fetchSoldoutDates(
            language,
            formatDate(fromDate),
            getExpositionGuidsByOrderLines(value),
            abortController.signal
        )
            .then((disabledDates) => {
                const disabledDatesList = disabledDates.map((d) => new Date(d));
                setDisabledDatesState(disabledDatesList);

                if (!dateString) {
                    setFirstAvailableDate(
                        disabledDatesList,
                        setSelectedDateState,
                        onDateChange
                    );
                }
                setIsLoadingDate(false);
            })
            .catch(() => {
                if (abortController.signal.aborted) return;
                setDisabledDatesState([]);
                setIsLoadingDate(false);
            });

        return () => abortController.abort();
    }, [activeStartDateState]);

    /**
     * Gets called when a date is selected.
     * @param e A `CustomEvent`, `target.value` is set to the selected date.
     */
    const onDateChange = (e: CustomEvent<{ value: Date }>) => {
        const date = formatDate(e.detail.value);
        setSelectedDateState(e.detail.value);
        const guids = getExpositionGuidsByOrderLines(value);
        const uniqueGuids = Array.from(new Set(guids));

        // For each guid we now want to fetch the prices and update the context with the prices
        const promises = uniqueGuids.map((guid) =>
            fetchExpositionTimeslotPrices(language, date, guid)
        );
        const updatedOrderLines = orderContext?.exposition_orderlines || [];

        Promise.all(promises)
            .then((timeSlotPricesArray) => {
                timeSlotPricesArray.forEach((timeSlotPrice) => {
                    const dynamicPriceGroups = Object.values(timeSlotPrice.prices)[0]; // Get the first (and only) record containing priceGroups.

                    if (!updatedOrderLines || !dynamicPriceGroups) {
                        const orderLines = (
                            orderContext?.exposition_orderlines || []
                        ).map((o) => ({
                            ...o,
                            exposition_period_guid: undefined,
                        }));
                        const event = inputChangeEventFactory(name, orderLines);
                        onChange && onChange(event);

                        return;
                    }

                    updatedOrderLines.forEach((orderLine, index) => {
                        const dynamicPriceGroup = dynamicPriceGroups.find(
                            (p) => p.group_guid === orderLine.exposition_price
                        );

                        // If a matching priceGroup is found, update the orderLine
                        if (dynamicPriceGroup) {
                            updatedOrderLines[index] = {
                                ...orderLine,
                                single_price: Number(dynamicPriceGroup.price),
                            };
                        }
                    });
                });

                const orderLines = updatedOrderLines.map((o) => ({
                    ...o,
                    exposition_period_guid: undefined,
                }));

                setOrderContext({
                    ...orderContext,
                    date: e.detail.value.toDateString(),
                    exposition_orderlines: orderLines,
                }); // Update the order context so that we trigger a re-render
            })
            .catch((error) => {
                console.error(error);
            });

        const event2 = inputChangeEventFactory('date', e.detail.value.toDateString());
        onChange && onChange(event2);
    };

    /**
     * Gets called when a timeslot is selected.
     * @param e A `CustomEvent`, `target.value` is set to the selected date.
     * @param expositionGuid
     * @param label
     */
    const onTimeSlotChange = (
        e: CustomEvent<IOption | null>,
        expositionGuid: string,
        label?: string
    ) => {
        const startTime = label?.split('-')[0];
        const incompleteOrderLine: IIncompleteOrderLine = {
            exposition: expositionGuid,
            exposition_period_guid: e.detail ? e.detail.value : undefined,
            start_time: startTime,
            type: 'venue',
        };

        const newOrderlines = mergeOrderLine(
            value,
            incompleteOrderLine,
            'exposition'
        ).map((newOrderLine) => {
            // We are making sure that the `single_price` is not removed from the orderline
            // As we update this in the `onDateChange` function, and otherwise we would be overwriting it.
            // Ideally, we could maybe add this in the mergeOrderLine, but that would require a lot of changes/testing
            const foundOrderLine = orderContext.exposition_orderlines?.find(
                (o) => o.exposition_price === newOrderLine.exposition_price
            );
            return {
                ...newOrderLine,
                single_price: foundOrderLine?.single_price,
            };
        });
        const event = inputChangeEventFactory(name, newOrderlines as Record<string, any>);
        onChange && onChange(event);
    };

    return (
        <Content>
            <H2 align='center'>{street.general_text.choose_day}</H2>
            <div className={styles.datePickerContainer}>
                <Datepicker
                    disabledDates={disabledDatesState}
                    isLoading={isLoadingDate}
                    label='Choose dates'
                    labelNext='Next'
                    labelPrevious='Previous'
                    name='date'
                    value={selectedDateState}
                    onActiveStartDateChange={({ activeStartDate }) =>
                        activeStartDate && setActiveStartDateState(activeStartDate)
                    }
                    onChange={onDateChange}
                />
            </div>
            <H2 align='center'>{street.general_text.available_times}</H2>
            <div className={styles.timePickerContainer}>
                {Array.from(venueOrderLinesByExposition).map(
                    ([expositionGuid, orderLines]) => (
                        <ExpositionTimeSlotPicker
                            key={expositionGuid}
                            label={street.general_text.time_for}
                            labelBold={
                                getVenueByExpositionGuid(street, expositionGuid)?.park
                                    ?.name
                            }
                            date={formatDate(selectedDateState)}
                            expositionGuid={expositionGuid}
                            orderLines={orderLines}
                            value={
                                orderLines.find(
                                    (o: IOrderLine | IIncompleteOrderLine) =>
                                        o.exposition_period_guid
                                )?.exposition_period_guid
                            }
                            onChange={(e, label) =>
                                onTimeSlotChange(e, expositionGuid, label)
                            }
                        />
                    )
                )}
            </div>
        </Content>
    );
};
